import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
} from "@/workshop/components/ui/context-menu";

/* ---------- Types ---------- */

export interface EnhancedFrameEntry {
  id: string;
  title: string;
  icon: LucideIcon;
  active?: boolean;
  statusColor?: string;
  isPinned?: boolean;
}

export interface FrameListEnhancedProps {
  frames: EnhancedFrameEntry[];
  onSelectFrame?: (frameId: string) => void;
  onRenameFrame?: (frameId: string) => void;
  onSetColorFrame?: (frameId: string) => void;
  onPinFrame?: (frameId: string) => void;
  onDuplicateFrame?: (frameId: string) => void;
  onDeleteFrame?: (frameId: string) => void;
  className?: string;
  "data-testid"?: string;
}

/* ---------- FrameListEnhanced ---------- */

export function FrameListEnhanced({
  frames,
  onSelectFrame,
  onRenameFrame,
  onSetColorFrame,
  onPinFrame,
  onDuplicateFrame,
  onDeleteFrame,
  className,
  "data-testid": testId,
}: FrameListEnhancedProps) {
  const sorted = [...frames].sort((a, b) => {
    if (a.isPinned && !b.isPinned) return -1;
    if (!a.isPinned && b.isPinned) return 1;
    return 0;
  });

  return (
    <div
      className={cn("flex flex-col", className)}
      data-testid={testId ?? "frame-list-enhanced"}
    >
      {sorted.map((frame) => (
        <FrameRow
          key={frame.id}
          frame={frame}
          onSelect={onSelectFrame}
          onRename={onRenameFrame}
          onSetColor={onSetColorFrame}
          onPin={onPinFrame}
          onDuplicate={onDuplicateFrame}
          onDelete={onDeleteFrame}
        />
      ))}
    </div>
  );
}

/* ---------- FrameRow ---------- */

interface FrameRowProps {
  frame: EnhancedFrameEntry;
  onSelect?: (id: string) => void;
  onRename?: (id: string) => void;
  onSetColor?: (id: string) => void;
  onPin?: (id: string) => void;
  onDuplicate?: (id: string) => void;
  onDelete?: (id: string) => void;
}

function FrameRow({
  frame,
  onSelect,
  onRename,
  onSetColor,
  onPin,
  onDuplicate,
  onDelete,
}: FrameRowProps) {
  const Icon = frame.icon;
  const statusColor = frame.statusColor ?? "bg-accent";

  return (
    <ContextMenu>
      <ContextMenuTrigger>
        <button
          type="button"
          onClick={() => onSelect?.(frame.id)}
          className={cn(
            "flex h-8 w-full items-center gap-2 px-4 transition-colors",
            frame.active
              ? "bg-glass-stroke-light"
              : "hover:bg-glass-stroke-subtle",
          )}
          data-testid={`frame-row-${frame.id}`}
        >
          <span
            className="h-3 w-0.5 shrink-0 rounded-[1px] bg-accent"
            aria-hidden="true"
          />
          <Icon
            className="h-3 w-3 shrink-0 text-muted"
            aria-hidden="true"
          />
          <span className="flex-1 truncate text-left text-[11px] text-secondary">
            {frame.title}
          </span>
          {frame.isPinned && (
            <Icons.Pin
              className="h-2.5 w-2.5 shrink-0 text-muted"
              aria-hidden="true"
            />
          )}
          <span
            className={cn("h-1.5 w-1.5 shrink-0 rounded-full", statusColor)}
            aria-hidden="true"
          />
        </button>
      </ContextMenuTrigger>

      <ContextMenuContent>
        <ContextMenuItem
          onClick={() => onRename?.(frame.id)}
          data-testid={`frame-ctx-rename-${frame.id}`}
        >
          <Icons.Edit className="h-3 w-3 text-muted" aria-hidden="true" />
          Rename
        </ContextMenuItem>

        <ContextMenuItem
          onClick={() => onSetColor?.(frame.id)}
          data-testid={`frame-ctx-color-${frame.id}`}
        >
          <Icons.Palette className="h-3 w-3 text-muted" aria-hidden="true" />
          Set color
        </ContextMenuItem>

        <ContextMenuItem
          onClick={() => onPin?.(frame.id)}
          data-testid={`frame-ctx-pin-${frame.id}`}
        >
          <Icons.Pin className="h-3 w-3 text-muted" aria-hidden="true" />
          {frame.isPinned ? "Unpin from top" : "Pin to top"}
        </ContextMenuItem>

        <ContextMenuItem
          onClick={() => onDuplicate?.(frame.id)}
          data-testid={`frame-ctx-duplicate-${frame.id}`}
        >
          <Icons.Copy className="h-3 w-3 text-muted" aria-hidden="true" />
          Duplicate
        </ContextMenuItem>

        <ContextMenuSeparator />

        <ContextMenuItem
          destructive
          onClick={() => onDelete?.(frame.id)}
          data-testid={`frame-ctx-delete-${frame.id}`}
        >
          <Icons.Trash2 className="h-3 w-3" aria-hidden="true" />
          Delete
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}
